import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import api, { errMsg } from '../../../lib/api';
import { Block } from './WeekBoard';
import { Check, ClipboardList, X } from 'lucide-react';

const dayName = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'numeric', day: 'numeric' });

const AddTask = ({ staffId, date, presets, onAdded }) => {
  const [title, setTitle] = useState('');
  const add = async (t) => {
    try { await api.post('/ludo/schedule/tasks', { staff_id: staffId, date, title: t }); setTitle(''); onAdded(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="text-[10px] text-[#5E5068] underline" data-testid={`sched-task-add-${staffId}-${date}`}>+ task</button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-2 space-y-1">
        {presets.map((p) => <button key={p} onClick={() => add(p)} className="block w-full text-left text-[12px] px-2 py-1 rounded hover:bg-[#F4F2F7]">{p}</button>)}
        <div className="flex gap-1 pt-1">
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Custom task" className="h-8 text-[12px]" data-testid="sched-task-custom" />
          <Button size="sm" disabled={!title.trim()} onClick={() => add(title)} className="h-8" data-testid="sched-task-custom-add">Add</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Rows = staff, columns = days: booked sessions, call-outs, open time and tasks for open time.
export const StaffBoard = ({ wk, days, staffName, onBlock, presets, reload }) => {
  const idx = wk.dates.map((_, i) => i).filter((i) => days.includes(['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'][i]));
  const toggle = async (t, upd) => { try { await api.patch(`/ludo/schedule/tasks/${t.task_id}`, upd); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="overflow-auto rounded-2xl border border-[#E5E0EA] bg-white" data-testid="sched-staff-board">
      <table className="min-w-full text-sm">
        <thead><tr className="bg-[#F7F5FA] text-[#6E5F78] text-[12px]">
          <th className="sticky left-0 bg-[#F7F5FA] text-left px-3 py-2 min-w-[180px]">Staff · hrs/wk</th>
          {idx.map((i) => <th key={i} className="px-2 py-2 text-left min-w-[150px]">{dayName(wk.dates[i])}</th>)}
        </tr></thead>
        <tbody>
          {wk.staff.map((s) => (
            <tr key={s.staff_id} className="border-t border-[#EEEAF2] align-top" data-testid={`sched-staff-row-${s.staff_id}`}>
              <td className="sticky left-0 bg-white px-3 py-2">
                <div className="font-medium text-[#2D2833]">{s.name} <span className="text-[10px] text-[#8A7B95]">{s.role}</span></div>
                <div className={`text-[11px] ${s.over_max ? 'text-[#8A3A3A]' : 'text-[#8A7B95]'}`}>{s.scheduled_hours} / {s.max_hours_week} hrs</div>
              </td>
              {idx.map((i) => {
                const d = s.days[i];
                const mine = wk.blocks.filter((b) => b.staff_id === s.staff_id && b.date === d.date && b.status !== 'cancelled');
                return (
                  <td key={i} className="px-1.5 py-1.5 space-y-1">
                    {d.out && <div className="rounded-lg bg-[#FFF6E5] border border-[#F0DDB0] text-[#8A6A1E] text-[11px] px-2 py-1" data-testid={`sched-out-${s.staff_id}-${d.date}`}>Out · {d.out.kind}</div>}
                    {mine.map((b) => <Block key={b.session_id} b={{ ...b, staff_id: b.client_name }} staffName={(x) => x} onClick={() => onBlock(b)} />)}
                    {d.free.map((f) => (
                      <div key={f.start} className="rounded-lg border border-dashed border-[#B7A6CC] text-[#6E5F78] text-[11px] px-2 py-1" data-testid={`sched-free-${s.staff_id}-${d.date}-${f.start}`}>
                        Open {f.start}–{f.end}
                      </div>
                    ))}
                    {d.tasks.map((t) => (
                      <div key={t.task_id} className="flex items-center gap-1 text-[11px] text-[#2D2833]" data-testid={`sched-task-${t.task_id}`}>
                        <button onClick={() => toggle(t, { done: !t.done })} className={`w-3.5 h-3.5 rounded border flex items-center justify-center ${t.done ? 'bg-[#87A096] border-[#87A096] text-white' : 'border-[#B7A6CC]'}`}>{t.done && <Check size={9} />}</button>
                        <ClipboardList size={10} className="text-[#8A7B95]" />
                        <span className={t.done ? 'line-through text-[#8A7B95]' : ''}>{t.title}</span>
                        <button onClick={() => toggle(t, { delete: true })} className="ml-auto text-[#8A7B95]"><X size={10} /></button>
                      </div>
                    ))}
                    {!d.out && d.free_hours > 0 && <AddTask staffId={s.staff_id} date={d.date} presets={presets} onAdded={reload} />}
                  </td>
                );
              })}
            </tr>
          ))}
          {wk.staff.length === 0 && <tr><td colSpan={idx.length + 1} className="p-8 text-center text-[#8A7B95]">Add BTs and BCBAs in the Staff tab first.</td></tr>}
        </tbody>
      </table>
      <span className="hidden">{staffName && ''}</span>
    </div>
  );
};

export default StaffBoard;
